<div
  class="search-wrapper"
  [dir]="directionality.value"
  [class.has-results]="
    (flatResults.length > 0 || hasActiveSearchTerm()) &&
    !isCommandMode &&
    !isAiMode
  "
  [class.has-category-results]="
    (flatResults.length > 0 || hasActiveSearchTerm()) &&
    isCategoryFilterMode &&
    selectedCategories.length > 0 &&
    !isAiMode
  "
  [class.is-ai-mode]="isAiMode"
  [class.search-wrapper--expanded]="isAiDialogExpanded"
>
  <!--
    In AI chat mode the composer owns input, so this row fades out and is taken
    out of flow (kept in the DOM so the palette state survives the round trip).
  -->
  <div
    class="input-overlay-wrapper"
    [class.no-ai-icon]="!isAiUiAgentEnabled"
    [class.input-overlay-wrapper-hidden]="isAiMode"
    [attr.aria-hidden]="isAiMode ? 'true' : null"
  >
    <span
      class="ui-icon-order-filter filter-icon c-p"
      [class.filter-icon-applied]="selectedCategories.length > 0"
      [matMenuTriggerFor]="categoryMenu"
      (click)="$event.stopPropagation()"
    ></span>
    <input
      #searchInput
      type="text"
      [(ngModel)]="searchTerm"
      (input)="onSearch()"
      (keydown)="onKeyDown($event)"
      (blur)="onInputBlur()"
      (focus)="onInputFocus()"
      [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_ANYTHING' | translate"
      [tabIndex]="isAiMode ? -1 : 0"
      class="search-input"
      autocomplete="off"
    />
    <div
      class="inline-hint"
      *ngIf="isCategoryFilterMode && getSuggestedSuffix()"
      [title]="
        'searchTerm=[' +
        searchTerm +
        '] suggestion=[' +
        getSuggestedSuffix() +
        ']'
      "
    >
      <span class="invisible-text">{{ searchTerm }}</span
      ><span class="gray-suggestion">{{ getSuggestedSuffix() }}</span>
    </div>

    <mat-menu #categoryMenu="matMenu" class="category-menu">
      <button
        mat-menu-item
        *ngFor="let category of availableCategories"
        (click)="toggleCategory(category, $event); $event.stopPropagation()"
      >
        <mat-checkbox [checked]="isSelected(category)">
          {{ translateCategory(category) }}
        </mat-checkbox>
      </button>
    </mat-menu>

    <span
      *ngIf="isAiUiAgent$ | async"
      class="ui-icon-star ai-toggle-icon c-p"
      [class.ai-toggle-icon--active]="isAiMode || isAiPromptDraft()"
      [matTooltip]="'SEARCH_GLOBAL_MODAL.AI_MODE_TOGGLE' | translate"
      (click)="toggleAiMode()"
    >
    </span>
    <span
      class="ui-icon-settings c-p settings-icon"
      (click)="openGlobalSearchSettingsDialog()"
    ></span>
  </div>

  <!--
    While a '?' question is being drafted the palette stays in search mode, so
    this hint is the only affordance telling the user Enter opens the chat.
  -->
  <div class="ai-draft-hint" *ngIf="isAiPromptDraft()">
    <span class="ui-icon-star ai-draft-hint-icon"></span>
    <span class="ai-draft-hint-text">{{
      (getAiPromptDraft()
        ? 'SEARCH_GLOBAL_MODAL.AI_DRAFT_PRESS_ENTER'
        : 'SEARCH_GLOBAL_MODAL.AI_DRAFT_TYPE_QUESTION'
      ) | translate
    }}</span>
  </div>

  <app-inline-chat-panel
    *ngIf="isAiMode"
    [composerFlipFromTop]="composerFlipFromTop"
    [initialPrompt]="aiInitialPrompt"
    [autoSubmitPrompt]="aiAutoSubmitPrompt"
    (backToSearch)="exitAiMode()"
    (toggleExpanded)="toggleAiDialogExpanded()"
    [isExpanded]="isAiDialogExpanded"
  ></app-inline-chat-panel>

  <!-- Reusable Camera List Template with Smart Virtual Scrolling -->
  <ng-template
    #cameraList
    let-cameras="cameras"
    let-showEdge="showEdge"
    let-showHeader="showHeader"
  >
    <div class="camera-list" *ngIf="cameras?.length">
      <div class="camera-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-camera"></i>
        <span
          >{{ cameras.length }}
          {{
            cameras.length === 1
              ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
              : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="camera-search-container"
        *ngIf="cameras.length > virtualScrollThresholds.CAMERA"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_CAMERAS' | translate"
          class="camera-search-input"
          [(ngModel)]="cameraSearchTerm"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="camera-list-container">
        <ng-container
          *ngIf="
            cameras.length > virtualScrollEnableThresholds.CAMERA;
            else singleCamera
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="
              getCameraListHeight(
                getFilteredCameras(cameras, cameraSearchTerm).length
              )
            "
            class="virtual-scroll-viewport"
          >
            <div
              class="camera-list-item"
              *cdkVirtualFor="
                let camera of getFilteredCameras(cameras, cameraSearchTerm);
                trackBy: trackCameraBy
              "
              (click)="navigateToCamera(camera)"
            >
              <div class="camera-snapshot">
                <ui-camera-snapshot
                  [cameraId]="camera.cameraId"
                  [edgeId]="camera.edgeId"
                ></ui-camera-snapshot>
              </div>
              <div class="camera-info">
                <div class="camera-name">
                  {{
                    camera.cameraName ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_CAMERA' | translate)
                  }}
                </div>
                <div class="camera-location">
                  {{ camera.locationName }}
                  <ng-container *ngIf="showEdge">
                    - {{ camera.edgeId }}
                  </ng-container>
                </div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single camera display -->
        <ng-template #singleCamera>
          <div class="camera-list-item" (click)="navigateToCamera(cameras[0])">
            <div class="camera-snapshot">
              <ui-camera-snapshot
                [cameraId]="cameras[0].cameraId"
                [edgeId]="cameras[0].edgeId"
              ></ui-camera-snapshot>
            </div>
            <div class="camera-info">
              <div class="camera-name">
                {{
                  cameras[0].cameraName ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_CAMERA' | translate)
                }}
              </div>
              <div class="camera-location">
                {{ cameras[0].locationName }}
                <ng-container *ngIf="showEdge">
                  - {{ cameras[0].edgeId }}
                </ng-container>
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Reusable Event List Template with Virtual Scrolling -->
  <ng-template #eventList let-events="events" let-showHeader="showHeader">
    <div class="event-list" *ngIf="events?.length">
      <div class="event-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-notifications"></i>
        <span
          >{{ events.length }}
          {{
            events.length === 1
              ? ('SEARCH_GLOBAL_MODAL.EVENT' | translate)
              : ('SEARCH_GLOBAL_MODAL.EVENTS' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="event-search-container"
        *ngIf="events.length > virtualScrollThresholds.EVENT"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_EVENTS' | translate"
          class="event-search-input"
          [(ngModel)]="eventSearchTerm"
          (input)="onEventSearchInput($event)"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="event-list-container">
        <ng-container
          *ngIf="
            events.length > virtualScrollEnableThresholds.EVENT;
            else singleEvent
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="getEventListHeight(events.length)"
            class="virtual-scroll-viewport event-viewport"
          >
            <div
              class="event-list-item"
              *cdkVirtualFor="
                let ev of getFilteredEvents(events, eventSearchTerm);
                trackBy: trackEventBy
              "
              (click)="navigateToEvent(ev)"
            >
              <div class="event-icon">
                <i class="icon ui-icon-notifications"></i>
              </div>
              <div class="event-info">
                <div class="event-name">
                  {{
                    ev.eventName ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_EVENT' | translate)
                  }}
                </div>
                <div class="event-sentence">
                  <app-event-sentence-renderer
                    [sentence]="ev.sentence"
                    [sentenceCameraInfo]="ev.sentenceCameraInfo"
                    [allBold]="true"
                  ></app-event-sentence-renderer>
                </div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single event display -->
        <ng-template #singleEvent>
          <div class="event-list-item" (click)="navigateToEvent(events[0])">
            <div class="event-icon">
              <i class="icon ui-icon-notifications"></i>
            </div>
            <div class="event-info">
              <div class="event-name">
                {{
                  events[0].eventName ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_EVENT' | translate)
                }}
              </div>
              <div class="event-sentence">
                <app-event-sentence-renderer
                  [sentence]="events[0].sentence"
                  [sentenceCameraInfo]="events[0].sentenceCameraInfo"
                  [allBold]="true"
                ></app-event-sentence-renderer>
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Reusable Edge List Template with Virtual Scrolling -->
  <ng-template #edgeList let-edges="edges" let-showHeader="showHeader">
    <div class="edge-list" *ngIf="edges?.length">
      <div class="edge-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-server"></i>
        <span
          >{{ edges.length }}
          {{
            edges.length === 1
              ? ('SEARCH_GLOBAL_MODAL.EDGE' | translate)
              : ('SEARCH_GLOBAL_MODAL.EDGES' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="edge-search-container"
        *ngIf="edges.length > virtualScrollThresholds.EDGE"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_EDGES' | translate"
          class="edge-search-input"
          [(ngModel)]="edgeSearchTerm"
          (input)="onEdgeSearchInput($event)"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="edge-list-container">
        <!-- Use virtual scrolling only for multiple edges -->
        <ng-container
          *ngIf="
            edges.length > virtualScrollEnableThresholds.EDGE;
            else singleEdge
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="400"
            class="virtual-scroll-viewport"
          >
            <div
              class="edge-list-item"
              *cdkVirtualFor="
                let edge of getFilteredEdges(edges, edgeSearchTerm);
                trackBy: trackEdgeBy
              "
              (click)="navigateToEdge(edge)"
            >
              <div class="edge-icon">
                <img
                  src="assets/devices/core.png"
                  [alt]="'SEARCH_GLOBAL_MODAL.EDGE' | translate"
                  class="edge-image"
                />
              </div>
              <div class="edge-info">
                <div class="edge-name">
                  {{
                    edge.edgeName ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_EDGE' | translate)
                  }}
                </div>
                <div class="edge-camera-count">
                  {{ edge.cameraCount || 0 }}
                  {{
                    (edge.cameraCount || 0) === 1
                      ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                      : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
                  }}
                </div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single edge display -->
        <ng-template #singleEdge>
          <div class="edge-list-item" (click)="navigateToEdge(edges[0])">
            <div class="edge-icon">
              <img
                src="assets/devices/core.png"
                [alt]="'SEARCH_GLOBAL_MODAL.EDGE' | translate"
                class="edge-image"
              />
            </div>
            <div class="edge-info">
              <div class="edge-name">
                {{
                  edges[0].edgeName ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_EDGE' | translate)
                }}
              </div>
              <div class="edge-camera-count">
                {{ edges[0].cameraCount || 0 }}
                {{
                  (edges[0].cameraCount || 0) === 1
                    ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                    : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
                }}
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Reusable Speaker List Template -->
  <ng-template #speakerList let-speakers="speakers" let-showHeader="showHeader">
    <div class="speaker-list" *ngIf="speakers?.length">
      <div class="speaker-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-volume-up"></i>
        <span
          >{{ speakers.length }}
          {{
            speakers.length === 1
              ? ('SEARCH_GLOBAL_MODAL.SPEAKER' | translate)
              : ('SEARCH_GLOBAL_MODAL.SPEAKERS' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="speaker-search-container"
        *ngIf="speakers.length > virtualScrollThresholds.SPEAKER"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_SPEAKERS' | translate"
          class="speaker-search-input"
          [(ngModel)]="speakerSearchTerm"
          (input)="onSpeakerSearchInput($event)"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="speaker-list-container">
        <!-- Use virtual scrolling only for multiple speakers -->
        <ng-container
          *ngIf="
            speakers.length > virtualScrollEnableThresholds.SPEAKER;
            else singleSpeaker
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="getSpeakerListHeight(speakers.length)"
            class="virtual-scroll-viewport"
          >
            <div
              class="speaker-list-item"
              *cdkVirtualFor="
                let speaker of getFilteredSpeakers(speakers, speakerSearchTerm);
                trackBy: trackSpeakerBy
              "
              (click)="navigateToSpeaker(speaker)"
            >
              <div class="speaker-icon">
                <img
                  src="assets/devices/speaker.png"
                  [alt]="'SEARCH_GLOBAL_MODAL.SPEAKER' | translate"
                  class="speaker-image"
                />
              </div>
              <div class="speaker-info">
                <div class="speaker-name">
                  {{
                    speaker.speakerName ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_SPEAKER' | translate)
                  }}
                </div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single speaker display -->
        <ng-template #singleSpeaker>
          <div
            class="speaker-list-item"
            (click)="navigateToSpeaker(speakers[0])"
          >
            <div class="speaker-icon">
              <img
                src="assets/devices/speaker.png"
                [alt]="'SEARCH_GLOBAL_MODAL.SPEAKER' | translate"
                class="speaker-image"
              />
            </div>
            <div class="speaker-info">
              <div class="speaker-name">
                {{
                  speakers[0].speakerName ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_SPEAKER' | translate)
                }}
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Reusable People List Template -->
  <ng-template #peopleList let-people="people" let-showHeader="showHeader">
    <div class="people-list" *ngIf="people?.length">
      <div class="people-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-person"></i>
        <span
          >{{ people.length }}
          {{
            people.length === 1
              ? ('SEARCH_GLOBAL_MODAL.PERSON' | translate)
              : ('SEARCH_GLOBAL_MODAL.PEOPLE' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="people-search-container"
        *ngIf="people.length > virtualScrollThresholds.PEOPLE"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_PEOPLE' | translate"
          class="people-search-input"
          [(ngModel)]="peopleSearchTerm"
          (input)="onPeopleSearchInput($event)"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="people-list-container">
        <!-- Use virtual scrolling only for multiple people -->
        <ng-container
          *ngIf="
            people.length > virtualScrollEnableThresholds.PEOPLE;
            else singlePerson
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="
              getPeopleListHeight(
                getFilteredPeople(people, peopleSearchTerm).length
              )
            "
            class="virtual-scroll-viewport"
          >
            <div
              class="people-list-item"
              *cdkVirtualFor="
                let person of getFilteredPeople(people, peopleSearchTerm);
                trackBy: trackPersonBy
              "
              (click)="navigateToPerson(person)"
            >
              <div class="person-image">
                <img
                  [appAuthSrc]="getPersonImagePath(person)"
                  [appAuthSrcFallback]="getPersonImageFallback(person)"
                  [alt]="person.name"
                  class="person-thumbnail"
                  (authError)="($event)"
                />
                <div class="person-placeholder" *ngIf="!person.bestImage">
                  <i class="icon ui-icon-person"></i>
                </div>
              </div>
              <div class="person-info">
                <div class="person-name">
                  {{
                    person.name ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_PERSON' | translate)
                  }}
                </div>
                <div class="person-details">
                  <span *ngIf="person.appearance && person.appearance > 0"
                    >{{ person.appearance }}
                    {{
                      person.appearance === 1
                        ? ('SEARCH_GLOBAL_MODAL.APPEARANCE' | translate)
                        : ('SEARCH_GLOBAL_MODAL.APPEARANCES' | translate)
                    }}</span
                  >
                  <span
                    *ngIf="!person.appearance || person.appearance === 0"
                    class="no-appearances"
                    >{{
                      'SEARCH_GLOBAL_MODAL.NO_RECOGNITIONS' | translate
                    }}</span
                  >
                </div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single person display -->
        <ng-template #singlePerson>
          <div class="people-list-item" (click)="navigateToPerson(people[0])">
            <div class="person-image">
              <img
                [appAuthSrc]="getPersonImagePath(people[0])"
                [appAuthSrcFallback]="getPersonImageFallback(people[0])"
                [alt]="people[0].name"
                class="person-thumbnail"
                (authError)="($event)"
              />
              <div class="person-placeholder" *ngIf="!people[0].bestImage">
                <i class="icon ui-icon-person"></i>
              </div>
            </div>
            <div class="person-info">
              <div class="person-name">
                {{
                  people[0].name ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_PERSON' | translate)
                }}
              </div>
              <div class="person-details">
                <span *ngIf="people[0].appearance && people[0].appearance > 0"
                  >{{ people[0].appearance }}
                  {{
                    people[0].appearance === 1
                      ? ('SEARCH_GLOBAL_MODAL.APPEARANCE' | translate)
                      : ('SEARCH_GLOBAL_MODAL.APPEARANCES' | translate)
                  }}</span
                >
                <span
                  *ngIf="!people[0].appearance || people[0].appearance === 0"
                  class="no-appearances"
                  >{{ 'SEARCH_GLOBAL_MODAL.NO_RECOGNITIONS' | translate }}</span
                >
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Reusable Building List Template -->
  <ng-template
    #buildingList
    let-buildings="buildings"
    let-showHeader="showHeader"
  >
    <div class="building-list" *ngIf="buildings?.length">
      <div class="building-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-building"></i>
        <span
          >{{ buildings.length }}
          {{
            buildings.length === 1
              ? ('SEARCH_GLOBAL_MODAL.BUILDING' | translate)
              : ('SEARCH_GLOBAL_MODAL.BUILDINGS' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="building-search-container"
        *ngIf="buildings.length > virtualScrollThresholds.BUILDING"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_BUILDINGS' | translate"
          class="building-search-input"
          [(ngModel)]="buildingSearchTerm"
          (input)="onBuildingSearchInput($event)"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="building-list-container">
        <!-- Use virtual scrolling only for multiple buildings -->
        <ng-container
          *ngIf="
            buildings.length > virtualScrollEnableThresholds.BUILDING;
            else singleBuilding
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="
              getBuildingListHeight(
                getFilteredBuildings(buildings, buildingSearchTerm).length
              )
            "
            class="virtual-scroll-viewport"
          >
            <div
              class="building-list-item"
              *cdkVirtualFor="
                let building of getFilteredBuildings(
                  buildings,
                  buildingSearchTerm
                );
                trackBy: trackBuildingBy
              "
              (click)="navigateToBuilding(building)"
            >
              <div class="building-icon">
                <i class="icon ui-icon-building"></i>
              </div>
              <div class="building-info">
                <div class="building-name">
                  {{
                    building.name ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_BUILDING' | translate)
                  }}
                </div>
                <div class="building-details">{{ building.subInfo }}</div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single building display -->
        <ng-template #singleBuilding>
          <div
            class="building-list-item"
            (click)="navigateToBuilding(buildings[0])"
          >
            <div class="building-icon">
              <i class="icon ui-icon-building"></i>
            </div>
            <div class="building-info">
              <div class="building-name">
                {{
                  buildings[0].name ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_BUILDING' | translate)
                }}
              </div>
              <div class="building-details">{{ buildings[0].subInfo }}</div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Reusable Floor List Template -->
  <ng-template
    #floorList
    let-floors="floors"
    let-showHeader="showHeader"
    let-buildingId="buildingId"
  >
    <div class="floor-list" *ngIf="floors?.length">
      <div class="floor-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-map"></i>
        <span
          >{{ floors.length }}
          {{
            floors.length === 1
              ? ('SEARCH_GLOBAL_MODAL.FLOOR' | translate)
              : ('SEARCH_GLOBAL_MODAL.FLOORS' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="floor-search-container"
        *ngIf="floors.length > virtualScrollThresholds.FLOOR"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_FLOORS' | translate"
          class="floor-search-input"
          [(ngModel)]="floorSearchTerm"
          (input)="onFloorSearchInput($event)"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="floor-list-container">
        <!-- Use virtual scrolling only for multiple floors -->
        <ng-container
          *ngIf="
            floors.length > virtualScrollEnableThresholds.FLOOR;
            else singleFloor
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="
              getFloorListHeight(
                getFilteredFloors(floors, floorSearchTerm).length
              )
            "
            class="virtual-scroll-viewport"
          >
            <div
              class="floor-list-item"
              *cdkVirtualFor="
                let floor of getFilteredFloors(floors, floorSearchTerm);
                trackBy: trackFloorBy
              "
              (click)="navigateToFloor(buildingId, floor.floorId)"
            >
              <div class="floor-icon">
                <i class="icon ui-icon-company"></i>
              </div>
              <div class="floor-info">
                <div class="floor-name">
                  {{
                    floor.name ||
                      floor.floorName ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_FLOOR' | translate)
                  }}
                </div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single floor display -->
        <ng-template #singleFloor>
          <div
            class="floor-list-item"
            (click)="navigateToFloor(buildingId, floors[0].floorId)"
          >
            <div class="floor-icon">
              <i class="icon ui-icon-map"></i>
            </div>
            <div class="floor-info">
              <div class="floor-name">
                {{
                  floors[0].name ||
                    floors[0].floorName ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_FLOOR' | translate)
                }}
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Reusable Sub-location List Template -->
  <ng-template
    #subLocationList
    let-subLocations="subLocations"
    let-showHeader="showHeader"
  >
    <div class="sublocation-list" *ngIf="subLocations?.length">
      <div class="sublocation-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-location"></i>
        <span
          >{{ subLocations.length }}
          {{
            subLocations.length === 1
              ? ('SEARCH_GLOBAL_MODAL.SUB_LOCATION' | translate)
              : ('SEARCH_GLOBAL_MODAL.SUB_LOCATIONS' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="sublocation-search-container"
        *ngIf="subLocations.length > virtualScrollThresholds.SUBLOCATION"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_SUB_LOCATIONS' | translate"
          class="sublocation-search-input"
          [(ngModel)]="subLocationSearchTerm"
          (input)="onSubLocationSearchInput($event)"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="sublocation-list-container">
        <!-- Use virtual scrolling only for multiple sub-locations -->
        <ng-container
          *ngIf="
            subLocations.length > virtualScrollEnableThresholds.SUBLOCATION;
            else singleSubLocation
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="getSubLocationListHeight(subLocations.length)"
            class="virtual-scroll-viewport"
          >
            <div
              class="sublocation-list-item"
              *cdkVirtualFor="
                let subLocation of getFilteredSubLocations(
                  subLocations,
                  subLocationSearchTerm
                );
                trackBy: trackSubLocationBy
              "
              (click)="navigateToSubLocation(subLocation)"
            >
              <div class="sublocation-icon">
                <i class="icon ui-icon-location"></i>
              </div>
              <div class="sublocation-info">
                <div class="sublocation-name">
                  {{
                    subLocation.subLocationName ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_SUB_LOCATION' | translate)
                  }}
                </div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single sub-location display -->
        <ng-template #singleSubLocation>
          <div
            class="sublocation-list-item"
            (click)="navigateToSubLocation(subLocations[0])"
          >
            <div class="sublocation-icon">
              <i class="icon ui-icon-location"></i>
            </div>
            <div class="sublocation-info">
              <div class="sublocation-name">
                {{
                  subLocations[0].subLocationName ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_SUB_LOCATION' | translate)
                }}
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Reusable Floor Navigation List Template -->
  <ng-template
    #floorNavigationList
    let-floors="floors"
    let-showHeader="showHeader"
  >
    <div class="floor-list" *ngIf="floors?.length">
      <div class="floor-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-map"></i>
        <span
          >{{ floors.length }}
          {{
            floors.length === 1
              ? ('SEARCH_GLOBAL_MODAL.FLOOR' | translate)
              : ('SEARCH_GLOBAL_MODAL.FLOORS' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="floor-search-container"
        *ngIf="floors.length > virtualScrollThresholds.FLOOR"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_FLOORS' | translate"
          class="floor-search-input"
          [(ngModel)]="floorNavigationSearchTerm"
          (input)="onFloorNavigationSearchInput($event)"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="floor-list-container">
        <!-- Use virtual scrolling only for multiple floors -->
        <ng-container
          *ngIf="
            floors.length > virtualScrollEnableThresholds.FLOOR;
            else singleFloorNav
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="
              getFloorNavigationListHeight(
                getFilteredFloorNavigation(floors, floorNavigationSearchTerm)
                  .length
              )
            "
            class="virtual-scroll-viewport"
          >
            <div
              class="floor-list-item"
              *cdkVirtualFor="
                let floor of getFilteredFloorNavigation(
                  floors,
                  floorNavigationSearchTerm
                );
                trackBy: trackFloorNavigationBy
              "
              (click)="navigateToFloorById(floor.floorId)"
            >
              <div class="floor-icon">
                <i class="icon ui-icon-company"></i>
              </div>
              <div class="floor-info">
                <div class="floor-name">
                  {{
                    floor.floorName ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_FLOOR' | translate)
                  }}
                </div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single floor display -->
        <ng-template #singleFloorNav>
          <div
            class="floor-list-item"
            (click)="navigateToFloorById(floors[0].floorId)"
          >
            <div class="floor-icon">
              <i class="icon ui-icon-map"></i>
            </div>
            <div class="floor-info">
              <div class="floor-name">
                {{
                  floors[0].floorName ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_FLOOR' | translate)
                }}
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Reusable Building Navigation List Template -->
  <ng-template
    #buildingNavigationList
    let-buildings="buildings"
    let-showHeader="showHeader"
  >
    <div class="building-list" *ngIf="buildings?.length">
      <div class="building-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-company"></i>
        <span
          >{{ buildings.length }}
          {{
            buildings.length === 1
              ? ('SEARCH_GLOBAL_MODAL.BUILDING' | translate)
              : ('SEARCH_GLOBAL_MODAL.BUILDINGS' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="building-search-container"
        *ngIf="buildings.length > virtualScrollThresholds.BUILDING"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_BUILDINGS' | translate"
          class="building-search-input"
          [(ngModel)]="buildingNavigationSearchTerm"
          (input)="onBuildingNavigationSearchInput($event)"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="building-list-container">
        <!-- Use virtual scrolling only for multiple buildings -->
        <ng-container
          *ngIf="
            buildings.length > virtualScrollEnableThresholds.BUILDING;
            else singleBuildingNav
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="
              getBuildingNavigationListHeight(
                getFilteredBuildingNavigation(
                  buildings,
                  buildingNavigationSearchTerm
                ).length
              )
            "
            class="virtual-scroll-viewport"
          >
            <div
              class="building-list-item"
              *cdkVirtualFor="
                let building of getFilteredBuildingNavigation(
                  buildings,
                  buildingNavigationSearchTerm
                );
                trackBy: trackBuildingNavigationBy
              "
              (click)="navigateToBuilding(building)"
            >
              <div class="building-icon">
                <i class="icon ui-icon-company"></i>
              </div>
              <div class="building-info">
                <div class="building-name">
                  {{
                    building.buildingName ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_BUILDING' | translate)
                  }}
                </div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single building display -->
        <ng-template #singleBuildingNav>
          <div
            class="building-list-item"
            (click)="navigateToBuilding(buildings[0])"
          >
            <div class="building-icon">
              <i class="icon ui-icon-company"></i>
            </div>
            <div class="building-info">
              <div class="building-name">
                {{
                  buildings[0].buildingName ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_BUILDING' | translate)
                }}
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Reusable Location List Template -->
  <ng-template
    #locationList
    let-locations="locations"
    let-showHeader="showHeader"
    let-useSearchNavigation="useSearchNavigation"
  >
    <div class="location-list" *ngIf="locations?.length">
      <div class="location-list-header" *ngIf="showHeader !== false">
        <i class="icon ui-icon-location"></i>
        <span
          >{{ locations.length }}
          {{
            locations.length === 1
              ? ('SEARCH_GLOBAL_MODAL.LOCATION' | translate)
              : ('SEARCH_GLOBAL_MODAL.LOCATIONS' | translate)
          }}</span
        >
      </div>

      <!-- Search Input -->
      <div
        class="location-search-container"
        *ngIf="locations.length > virtualScrollThresholds.LOCATION"
      >
        <input
          type="text"
          [placeholder]="'SEARCH_GLOBAL_MODAL.SEARCH_LOCATIONS' | translate"
          class="location-search-input"
          [(ngModel)]="locationSearchTerm"
          (input)="onLocationSearchInput($event)"
          (click)="$event.stopPropagation()"
        />
      </div>
      <div class="location-list-container">
        <!-- Use virtual scrolling only for multiple locations -->
        <ng-container
          *ngIf="
            locations.length > virtualScrollEnableThresholds.LOCATION;
            else singleLocation
          "
        >
          <cdk-virtual-scroll-viewport
            [itemSize]="80"
            [style.height.px]="getLocationListHeight(locations.length)"
            class="virtual-scroll-viewport"
          >
            <div
              class="location-list-item"
              *cdkVirtualFor="
                let location of getFilteredLocations(
                  locations,
                  locationSearchTerm
                );
                trackBy: trackLocationBy
              "
              (click)="
                useSearchNavigation
                  ? navigateToLocationByName(location.locationName)
                  : navigateToLocationById(location.locationId)
              "
            >
              <div class="location-icon">
                <i class="icon ui-icon-location"></i>
              </div>
              <div class="location-info">
                <div class="location-name">
                  {{
                    location.locationName ||
                      ('SEARCH_GLOBAL_MODAL.UNNAMED_LOCATION' | translate)
                  }}
                </div>
              </div>
            </div>
          </cdk-virtual-scroll-viewport>
        </ng-container>

        <!-- Single location display -->
        <ng-template #singleLocation>
          <div
            class="location-list-item"
            (click)="
              useSearchNavigation
                ? navigateToLocationByName(locations[0].locationName)
                : navigateToLocationById(locations[0].locationId)
            "
          >
            <div class="location-icon">
              <i class="icon ui-icon-location"></i>
            </div>
            <div class="location-info">
              <div class="location-name">
                {{
                  locations[0].locationName ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_LOCATION' | translate)
                }}
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  </ng-template>

  <!-- Command Mode UI -->
  <div class="command-mode-container" *ngIf="isCommandMode">
    <div class="command-preview">
      <div class="command-icon">
        <i class="icon ui-icon-search"></i>
      </div>
      <div class="command-info">
        <div class="command-title">
          {{ 'SEARCH_GLOBAL_MODAL.ADVANCED_SEARCH' | translate }}
        </div>
        <div class="command-description">
          {{ 'SEARCH_GLOBAL_MODAL.SEARCH_DESCRIPTION' | translate }}
        </div>
        <div class="command-search-term">
          {{ 'SEARCH_GLOBAL_MODAL.SEARCHING_FOR' | translate }} "{{
            searchTerm.substring(1)
          }}"
        </div>
      </div>
    </div>
  </div>

  <!-- Category Filter Mode UI -->
  <div
    class="category-filter-mode-container"
    *ngIf="isCategoryFilterMode"
    (mousedown)="onAutocompleteMouseDown($event)"
  >
    <div class="category-filter-preview">
      <div class="category-filter-icon">
        <i class="icon ui-icon-filter"></i>
      </div>
      <div class="category-filter-info">
        <div class="category-filter-title">
          {{ 'SEARCH_GLOBAL_MODAL.CATEGORY_FILTERS' | translate }}
        </div>
        <div
          class="category-filter-description"
          *ngIf="selectedCategories.length === 0"
        >
          {{ 'SEARCH_GLOBAL_MODAL.CATEGORY_FILTER_DESCRIPTION' | translate }}
        </div>
        <div
          class="category-filter-description"
          *ngIf="selectedCategories.length > 0"
        >
          {{ 'SEARCH_GLOBAL_MODAL.SEARCH_FOR' | translate }}
          <ng-container *ngFor="let cat of selectedCategories; let i = index">
            <strong class="badge" [class]="'badge-' + getBadgeType(cat)">{{
              getTypeI18nKey(cat) | translate
            }}</strong
            ><span *ngIf="i < selectedCategories.length - 1">, </span>
          </ng-container>
          {{ 'SEARCH_GLOBAL_MODAL.ACROSS_THE_ENTIRE_SYSTEM' | translate }}
        </div>
        <div
          class="category-filter-search-term subtle"
          *ngIf="selectedCategories.length > 0 && getCategorySearchTerm()"
        >
          Searching for: "{{ getCategorySearchTerm() }}"
        </div>
        <div
          class="category-filter-hint subtle"
          *ngIf="selectedCategories.length === 0 && !showCategoryAutocomplete"
        >
          <small
            >{{ 'SEARCH_GLOBAL_MODAL.AVAILABLE_CATEGORIES' | translate }}
            {{ getFormattedCategoriesString() }}</small
          >
        </div>
      </div>
    </div>

    <!-- Category Autocomplete Dropdown -->
    <div
      class="category-autocomplete"
      *ngIf="isAutocompleteVisible()"
      (mousedown)="onAutocompleteMouseDown($event)"
    >
      <div class="autocomplete-header" *ngIf="filteredCategories.length > 0">
        {{ 'SEARCH_GLOBAL_MODAL.AVAILABLE_CATEGORIES_HEADER' | translate }}
      </div>
      <ul class="autocomplete-list" *ngIf="filteredCategories.length > 0">
        <li
          *ngFor="let category of filteredCategories; let i = index"
          class="autocomplete-item"
          [class.active]="i === activeCategoryIndex"
          (click)="selectCategory(category)"
        >
          <span class="badge" [class]="'badge-' + getBadgeType(category)">{{
            getTypeI18nKey(category) | translate
          }}</span>
        </li>
      </ul>
      <div class="no-categories-found" *ngIf="filteredCategories.length === 0">
        {{ 'SEARCH_GLOBAL_MODAL.NO_CATEGORIES_FOUND' | translate }} "{{
          getCategoryInput()
        }}"
      </div>
    </div>
  </div>

  <!-- Search Results (for both regular and category filter modes) -->
  <div
    class="search-results-container"
    *ngIf="flatResults.length > 0 && !isCommandMode"
    (mouseleave)="onSearchResultsMouseLeave()"
  >
    <div class="results-container">
      <ng-container *ngFor="let categoryKey of getOrderedCategoryKeys()">
        <!-- Show category header only if not filtering or if category matches -->
        <div
          class="category-header"
          *ngIf="
            (!isCategoryFilterMode ||
              selectedCategories.includes(categoryKey)) &&
            groupedResults[categoryKey]?.length > 0
          "
        >
          {{ getTypeI18nKey(categoryKey) | translate }}
        </div>
        <!-- Show results only if not filtering or if category matches -->
        <ul
          class="results-list"
          *ngIf="
            (!isCategoryFilterMode ||
              selectedCategories.includes(categoryKey)) &&
            groupedResults[categoryKey]?.length > 0
          "
        >
          <li
            *ngFor="let result of groupedResults[categoryKey]"
            class="result-item"
            (click)="navigateToResult(result)"
            (mouseenter)="onResultHover(result)"
            (mouseleave)="onResultLeave()"
            [class.active]="flatResults[activeIndex] === result"
          >
            <div class="result-content">
              <span
                class="badge"
                [class]="'badge-' + getBadgeType(result.type)"
                >{{ getTypeI18nKey(result.type) | translate }}</span
              >
              <div class="result-visual">
                <img
                  *ngIf="result.icon && isImageUrl(result.icon)"
                  [appAuthSrc]="result.icon"
                  [alt]="result.name"
                  class="result-image"
                />
                <i
                  *ngIf="result.icon && !isImageUrl(result.icon)"
                  [class]="result.icon"
                  class="result-icon-class"
                ></i>
              </div>
              <div class="result-text">
                <div
                  class="result-title"
                  [innerHTML]="
                    highlightMatchByFuseResult(result.name, result, 'name')
                  "
                ></div>
                <div
                  class="result-subinfo"
                  [innerHTML]="
                    highlightMatchByFuseResult(
                      result.subInfo || result.description || '',
                      result,
                      'subInfo'
                    )
                  "
                ></div>
                <!-- Match reason -->
                <div
                  class="result-match-reason subtle"
                  *ngIf="getMatchReason(result)"
                >
                  <span
                    [innerHTML]="getMatchReasonWithHighlighting(result)"
                  ></span>
                  <!-- Badge showing additional matches -->
                  <span
                    class="match-badge"
                    *ngIf="getAllMatchReasons(result).length > 1"
                    (mouseenter)="showMatchReasonsTooltip(result, $event)"
                    (mouseleave)="hideMatchReasonsTooltip()"
                  >
                    +{{ getAllMatchReasons(result).length - 1 }}
                  </span>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </ng-container>
    </div>

    <div
      class="preview-panel"
      *ngIf="previewResult"
      (mouseenter)="onPreviewMouseEnter()"
      (mouseleave)="onPreviewMouseLeave()"
    >
      <!-- Camera Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'camera'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.CAMERA_PANEL' | translate }}</h4>
        <ui-camera-snapshot
          [cameraId]="previewResult.cameraId"
          [edgeId]="previewResult.edgeId"
        ></ui-camera-snapshot>
        <div class="camera-snapshot-spacer"></div>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-status">
              <ui-camera-status
                [cameraId]="previewResult.cameraId"
              ></ui-camera-status>
            </div>
            <div class="preview-name">{{ previewResult.name }}</div>
            <div class="preview-actions">
              <span
                class="ui-icon-object-analytic action-icon"
                [matTooltip]="'LOCATION_LIST.CAMERA_HEALTH' | translate"
                matTooltipClass="sidebar-tooltip"
                (click)="
                  openCameraHealth(previewResult); $event.stopPropagation()
                "
              >
              </span>
              <button
                class="ui-icon-edit ui-icon-button no-border icon-hover"
                disableButtonPermissionGuard
                [permission]="permissions.CameraEdit"
                [entityIds]="[
                  previewResult.cameraId,
                  previewResult.edgeId,
                  previewResult.locationId,
                ]"
                [tooltip]="'SEARCH_GLOBAL_MODAL.EDIT_CAMERA' | translate"
                [disabledTooltip]="
                  'SEARCH_GLOBAL_MODAL.NO_EDIT_PERMISSIONS' | translate
                "
                matTooltip
                matTooltipClass="sidebar-tooltip"
                [matMenuTriggerFor]="cameraEditMenu"
              ></button>
                <mat-menu #cameraEditMenu="matMenu" class="mat-dropdown-panel camera-edit-dropdown">
                    @for (tab of cameraEditTabsSig(); track tab.key) {

                        @if (!tab.children?.length) {
                            <button
                                    mat-menu-item
                                    (click)="editCamera(previewResult, tab.route)">
                                {{ tab.name | translate }}
                            </button>
                        } @else {
                            <button
                                    mat-menu-item
                                    [matMenuTriggerFor]="childMenu">
                                {{ tab.name | translate }}
                            </button>

                            <mat-menu #childMenu="matMenu">
                                @for (child of tab.children; track child.key) {
                                    <button
                                            mat-menu-item
                                            (click)="editCamera(previewResult, child.route)">
                                        {{ child.name | translate }}
                                    </button>
                                }
                            </mat-menu>
                        }

                    }
                </mat-menu>
            </div>
          </div>
          <div class="preview-location" *ngIf="previewResult.raw?.address">
            {{ previewResult.raw.address }}
          </div>

          <!-- Camera Hierarchy Navigation (Compact) -->
          <div class="camera-hierarchy">
            <!-- Location -->
            <ng-container
              *ngTemplateOutlet="
                locationList;
                context: {
                  locations: getCameraLocations(previewResult),
                  showHeader: true,
                  useSearchNavigation: true,
                }
              "
            ></ng-container>

            <!-- Sub-location -->
            <ng-container
              *ngTemplateOutlet="
                subLocationList;
                context: {
                  subLocations: previewResult.raw?.subLocations,
                  showHeader: true,
                }
              "
            ></ng-container>

            <!-- Edge/Core -->
            <ng-container
              *ngTemplateOutlet="
                edgeList;
                context: {
                  edges: getCameraEdges(previewResult),
                  showHeader: true,
                }
              "
            ></ng-container>

            <!-- Buildings -->
            <div
              *ngIf="
                previewResult.raw?.buildings &&
                previewResult.raw.buildings.length > 0
              "
            >
              <ng-container
                *ngTemplateOutlet="
                  buildingNavigationList;
                  context: {
                    buildings: previewResult.raw?.buildings,
                    showHeader: true,
                  }
                "
              ></ng-container>
            </div>

            <!-- Floors -->
            <div
              *ngIf="
                previewResult.raw?.floors && previewResult.raw.floors.length > 0
              "
            >
              <ng-container
                *ngTemplateOutlet="
                  floorNavigationList;
                  context: {
                    floors: previewResult.raw?.floors,
                    showHeader: true,
                  }
                "
              ></ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- Location Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'location'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.LOCATION_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">{{ previewResult.name }}</div>
            <div class="preview-actions">
              <span
                class="ui-icon-health action-icon"
                [matTooltip]="
                  'LOCATION_LIST.BUTTONS.LOCATION_HEALTH' | translate
                "
                matTooltipClass="sidebar-tooltip"
                (click)="
                  openLocationHealth(previewResult); $event.stopPropagation()
                "
              >
              </span>
              <button
                class="ui-icon-edit ui-icon-button no-border"
                disableButtonPermissionGuard
                [permission]="permissions.LocationEdit"
                [entityIds]="[previewResult.locationId]"
                [tooltip]="'SEARCH_GLOBAL_MODAL.EDIT_LOCATION' | translate"
                [disabledTooltip]="
                  'SEARCH_GLOBAL_MODAL.NO_EDIT_PERMISSIONS' | translate
                "
                matTooltip
                matTooltipClass="sidebar-tooltip"
                (click)="editLocation(previewResult)"
              ></button>
            </div>
          </div>
          <div class="preview-location" *ngIf="previewResult.raw?.address">
            {{ previewResult.raw.address }}
          </div>

          <!-- Summary Stats -->
          <div class="location-summary">
            <div
              class="summary-item"
              *ngIf="getLocationEdges(previewResult).length > 0"
              [class.active]="activeLocationView === 'edges'"
              (click)="setActiveLocationView('edges'); $event.stopPropagation()"
            >
              <span class="count">{{
                getLocationEdges(previewResult).length
              }}</span>
              <span class="label">{{
                getLocationEdges(previewResult).length === 1
                  ? ('SEARCH_GLOBAL_MODAL.EDGE' | translate)
                  : ('SEARCH_GLOBAL_MODAL.EDGES' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.cameras?.length > 0"
              [class.active]="activeLocationView === 'cameras'"
              (click)="
                setActiveLocationView('cameras'); $event.stopPropagation()
              "
            >
              <span class="count">{{ previewResult.raw.cameras.length }}</span>
              <span class="label">{{
                previewResult.raw.cameras.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                  : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="getLocationSpeakers(previewResult).length > 0"
              [class.active]="activeLocationView === 'speakers'"
              (click)="
                setActiveLocationView('speakers'); $event.stopPropagation()
              "
            >
              <span class="count">{{
                getLocationSpeakers(previewResult).length
              }}</span>
              <span class="label">{{
                getLocationSpeakers(previewResult).length === 1
                  ? ('SEARCH_GLOBAL_MODAL.SPEAKER' | translate)
                  : ('SEARCH_GLOBAL_MODAL.SPEAKERS' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="getLocationSubLocations(previewResult).length > 0"
              [class.active]="activeLocationView === 'sublocations'"
              (click)="
                setActiveLocationView('sublocations'); $event.stopPropagation()
              "
            >
              <span class="count">{{
                getLocationSubLocations(previewResult).length
              }}</span>
              <span class="label">{{
                getLocationSubLocations(previewResult).length === 1
                  ? ('SEARCH_GLOBAL_MODAL.SUB_LOCATION' | translate)
                  : ('SEARCH_GLOBAL_MODAL.SUB_LOCATIONS' | translate)
              }}</span>
            </div>
          </div>

          <!-- Active List Display -->
          <div class="location-content">
            <!-- Edge List -->
            <div *ngIf="activeLocationView === 'edges'">
              <ng-container
                *ngTemplateOutlet="
                  edgeList;
                  context: {
                    edges: getLocationEdges(previewResult),
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Camera List -->
            <div *ngIf="activeLocationView === 'cameras'">
              <ng-container
                *ngTemplateOutlet="
                  cameraList;
                  context: {
                    cameras: previewResult.raw?.cameras,
                    showEdge: false,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Speaker List -->
            <div *ngIf="activeLocationView === 'speakers'">
              <ng-container
                *ngTemplateOutlet="
                  speakerList;
                  context: {
                    speakers: getLocationSpeakers(previewResult),
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Sub-location List -->
            <div *ngIf="activeLocationView === 'sublocations'">
              <ng-container
                *ngTemplateOutlet="
                  subLocationList;
                  context: {
                    subLocations: getLocationSubLocations(previewResult),
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- Edge/Core Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'core'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.CORE_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">{{ previewResult.name }}</div>
            <div class="preview-actions">
              <button
                class="ui-icon-add-camera ui-icon-button no-border"
                (click)="
                  addCameraToEdge(previewResult); $event.stopPropagation()
                "
                [matTooltip]="'SEARCH_GLOBAL_MODAL.ADD_CAMERAS' | translate"
                matTooltipClass="sidebar-tooltip"
              ></button>
              <button
                class="ui-icon-edit ui-icon-button no-border"
                disableButtonPermissionGuard
                [permission]="permissions.CoreEdit"
                [entityIds]="[previewResult.edgeId, previewResult.locationId]"
                [tooltip]="'SEARCH_GLOBAL_MODAL.EDIT_EDGE' | translate"
                [disabledTooltip]="
                  'SEARCH_GLOBAL_MODAL.NO_EDIT_PERMISSIONS' | translate
                "
                matTooltip
                matTooltipClass="sidebar-tooltip"
                (click)="editEdge(previewResult)"
              ></button>
            </div>
          </div>
          <div class="preview-description" *ngIf="previewResult.subInfo">
            {{ previewResult.subInfo }}
          </div>
          <div class="preview-location" *ngIf="previewResult.raw?.address">
            {{ previewResult.raw.address }}
          </div>

          <!-- Core Hierarchy (Compact) -->
          <div class="core-hierarchy">
            <!-- Location -->
            <ng-container
              *ngTemplateOutlet="
                locationList;
                context: {
                  locations: getEdgeLocations(previewResult),
                  showHeader: true,
                  useSearchNavigation: true,
                }
              "
            ></ng-container>

            <!-- Cameras -->
            <ng-container
              *ngTemplateOutlet="
                cameraList;
                context: {
                  cameras: previewResult.raw?.cameras,
                  showEdge: false,
                  showHeader: true,
                }
              "
            ></ng-container>
          </div>
        </div>
      </div>

      <!-- Event Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'event'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.EVENT_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">
              <ui-toggle
                (click)="$event.stopPropagation()"
                [matTooltip]="
                  !!previewResult.enabled
                    ? ('ALERT_LIST.DISABLE_ALERT' | translate)
                    : ('ALERT_LIST.ENABLE_ALERT' | translate)
                "
                matTooltipClass="sidebar-tooltip"
                matTooltipPosition="right"
                [value]="previewResult.enabled"
                (change)="enable(previewResult, $event)"
                disableButtonPermissionGuard
                [permission]="permissions.EventEdit"
                [disabledTooltip]="
                  'SEARCH_GLOBAL_MODAL.NO_EDIT_PERMISSIONS' | translate
                "
              >
              </ui-toggle>
              {{ previewResult.name }}
            </div>
            <div class="preview-actions">
              <button
                class="ui-icon-edit ui-icon-button no-border"
                disableButtonPermissionGuard
                [permission]="permissions.EventEdit"
                [tooltip]="'SEARCH_GLOBAL_MODAL.EDIT_EVENT' | translate"
                [disabledTooltip]="
                  'SEARCH_GLOBAL_MODAL.NO_EDIT_PERMISSIONS' | translate
                "
                matTooltip
                matTooltipClass="sidebar-tooltip"
                (click)="editEvent(previewResult)"
              ></button>
            </div>
          </div>
          <div class="preview-description" *ngIf="previewResult.subInfo">
            {{ previewResult.subInfo }}
          </div>
          <app-event-sentence-renderer
            [sentence]="previewResult.sentence"
            [sentenceCameraInfo]="previewResult.sentenceCameraInfo"
            [allBold]="true"
          ></app-event-sentence-renderer>

          <!-- Interactive Navigation -->
          <div class="location-summary">
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.edges?.length > 0"
              [class.active]="activeEventView === 'edges'"
              (click)="setActiveEventView('edges'); $event.stopPropagation()"
            >
              <span class="count">{{ previewResult.raw.edges.length }}</span>
              <span class="label">{{
                previewResult.raw.edges.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.EDGE' | translate)
                  : ('SEARCH_GLOBAL_MODAL.EDGES' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.cameras?.length > 0"
              [class.active]="activeEventView === 'cameras'"
              (click)="setActiveEventView('cameras'); $event.stopPropagation()"
            >
              <span class="count">{{ previewResult.raw.cameras.length }}</span>
              <span class="label">{{
                previewResult.raw.cameras.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                  : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
              }}</span>
            </div>
          </div>

          <!-- Active List Display -->
          <div class="location-content">
            <!-- Edge List -->
            <div *ngIf="activeEventView === 'edges'">
              <ng-container
                *ngTemplateOutlet="
                  edgeList;
                  context: {
                    edges: previewResult.raw?.edges,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Camera List -->
            <div *ngIf="activeEventView === 'cameras'">
              <ng-container
                *ngTemplateOutlet="
                  cameraList;
                  context: {
                    cameras: previewResult.raw?.cameras,
                    showEdge: false,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- Sub-Location Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'sublocation'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.SUB_LOCATION_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">{{ previewResult.name }}</div>
          </div>
          <div class="preview-description" *ngIf="previewResult.subInfo">
            {{ previewResult.subInfo }}
          </div>

          <!-- Summary Stats -->
          <div class="location-summary">
            <div
              class="summary-item"
              *ngIf="getSubLocationEdges(previewResult).length > 0"
              [class.active]="activeSubLocationView === 'edges'"
              (click)="
                setActiveSubLocationView('edges'); $event.stopPropagation()
              "
            >
              <span class="count">{{
                getSubLocationEdges(previewResult).length
              }}</span>
              <span class="label">{{
                getSubLocationEdges(previewResult).length === 1
                  ? ('SEARCH_GLOBAL_MODAL.EDGE' | translate)
                  : ('SEARCH_GLOBAL_MODAL.EDGES' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.cameras?.length > 0"
              [class.active]="activeSubLocationView === 'cameras'"
              (click)="
                setActiveSubLocationView('cameras'); $event.stopPropagation()
              "
            >
              <span class="count">{{ previewResult.raw.cameras.length }}</span>
              <span class="label">{{
                previewResult.raw.cameras.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                  : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="getSubLocationSpeakers(previewResult).length > 0"
              [class.active]="activeSubLocationView === 'speakers'"
              (click)="
                setActiveSubLocationView('speakers'); $event.stopPropagation()
              "
            >
              <span class="count">{{
                getSubLocationSpeakers(previewResult).length
              }}</span>
              <span class="label">{{
                getSubLocationSpeakers(previewResult).length === 1
                  ? ('SEARCH_GLOBAL_MODAL.SPEAKER' | translate)
                  : ('SEARCH_GLOBAL_MODAL.SPEAKERS' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="getSubLocationSubLocations(previewResult).length > 0"
              [class.active]="activeSubLocationView === 'sublocations'"
              (click)="
                setActiveSubLocationView('sublocations');
                $event.stopPropagation()
              "
            >
              <span class="count">{{
                getSubLocationSubLocations(previewResult).length
              }}</span>
              <span class="label">{{
                getSubLocationSubLocations(previewResult).length === 1
                  ? ('SEARCH_GLOBAL_MODAL.SUB_LOCATION' | translate)
                  : ('SEARCH_GLOBAL_MODAL.SUB_LOCATIONS' | translate)
              }}</span>
            </div>
          </div>

          <!-- Active List Display -->
          <div class="location-content">
            <!-- Edge List -->
            <div *ngIf="activeSubLocationView === 'edges'">
              <ng-container
                *ngTemplateOutlet="
                  edgeList;
                  context: {
                    edges: getSubLocationEdges(previewResult),
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Camera List -->
            <div *ngIf="activeSubLocationView === 'cameras'">
              <ng-container
                *ngTemplateOutlet="
                  cameraList;
                  context: {
                    cameras: previewResult.raw?.cameras,
                    showEdge: false,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Speaker List -->
            <div *ngIf="activeSubLocationView === 'speakers'">
              <ng-container
                *ngTemplateOutlet="
                  speakerList;
                  context: {
                    speakers: getSubLocationSpeakers(previewResult),
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Sub-location List -->
            <div *ngIf="activeSubLocationView === 'sublocations'">
              <ng-container
                *ngTemplateOutlet="
                  subLocationList;
                  context: {
                    subLocations: getSubLocationSubLocations(previewResult),
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- Speaker Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'speaker'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.SPEAKER_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">{{ previewResult.name }}</div>
            <div class="preview-actions">
              <button
                class="ui-icon-edit ui-icon-button no-border"
                (click)="editSpeaker(previewResult)"
                [matTooltip]="'SEARCH_GLOBAL_MODAL.EDIT_SPEAKER' | translate"
                matTooltipClass="sidebar-tooltip"
              ></button>
            </div>
          </div>
          <div class="preview-description" *ngIf="previewResult.raw?.model">
            Model: {{ previewResult.raw.model }}
          </div>
          <div class="preview-sip" *ngIf="previewResult.raw?.sipUrl">
            <div class="sip-label">
              {{ 'SEARCH_GLOBAL_MODAL.SIP_URL' | translate }}
            </div>
            <div class="sip-content">
              <span class="sip-value" uiTruncateText>{{
                previewResult.raw.sipUrl
              }}</span>
              <ui-copy-clipboard
                [copyContent]="previewResult.raw.sipUrl"
                [fontSize]="14"
                [toolTip]="'SEARCH_GLOBAL_MODAL.COPY_SIP_URL' | translate"
              >
              </ui-copy-clipboard>
            </div>
          </div>

          <!-- Interactive Navigation -->
          <div
            class="location-summary"
            *ngIf="
              getSpeakerEdges(previewResult)?.length > 0 ||
              previewResult.raw?.cameras?.length > 0
            "
          >
            <div
              class="summary-item"
              *ngIf="getSpeakerEdges(previewResult)?.length > 0"
              [class.active]="activeSpeakerView === 'edges'"
              (click)="setActiveSpeakerView('edges'); $event.stopPropagation()"
            >
              <span class="count">{{
                getSpeakerEdges(previewResult).length
              }}</span>
              <span class="label">{{
                getSpeakerEdges(previewResult).length === 1
                  ? ('SEARCH_GLOBAL_MODAL.EDGE' | translate)
                  : ('SEARCH_GLOBAL_MODAL.EDGES' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.cameras?.length > 0"
              [class.active]="activeSpeakerView === 'cameras'"
              (click)="
                setActiveSpeakerView('cameras'); $event.stopPropagation()
              "
            >
              <span class="count">{{ previewResult.raw.cameras.length }}</span>
              <span class="label">{{
                previewResult.raw.cameras.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                  : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
              }}</span>
            </div>
          </div>

          <!-- Active List Display -->
          <div
            class="location-content"
            *ngIf="
              getSpeakerEdges(previewResult)?.length > 0 ||
              previewResult.raw?.cameras?.length > 0
            "
          >
            <!-- Edge List -->
            <div *ngIf="activeSpeakerView === 'edges'">
              <ng-container
                *ngTemplateOutlet="
                  edgeList;
                  context: {
                    edges: getSpeakerEdges(previewResult),
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Camera List -->
            <div *ngIf="activeSpeakerView === 'cameras'">
              <ng-container
                *ngTemplateOutlet="
                  cameraList;
                  context: {
                    cameras: previewResult.raw?.cameras,
                    showEdge: false,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- Person Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'person'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.PERSON_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="person-profile-card">
            <div class="person-avatar-container">
              <img
                [appAuthSrc]="getPersonImagePath(previewResult)"
                [appAuthSrcFallback]="getPersonImageFallback(previewResult)"
                [alt]="previewResult.name"
                class="person-avatar"
                (authError)="($event)"
              />
              <div
                class="person-avatar-placeholder"
                *ngIf="!previewResult.bestImage"
              >
                <i class="icon ui-icon-person"></i>
              </div>
              <div class="person-avatar-actions">
                <button
                  class="ui-icon-edit ui-icon-button no-border"
                  (click)="editPerson(previewResult)"
                  [matTooltip]="'SEARCH_GLOBAL_MODAL.EDIT_PERSON' | translate"
                  matTooltipClass="sidebar-tooltip"
                ></button>
                <button
                  class="ui-icon-delete ui-icon-button no-border"
                  (click)="deletePerson(previewResult)"
                  [matTooltip]="'SEARCH_GLOBAL_MODAL.DELETE_PERSON' | translate"
                  matTooltipClass="sidebar-tooltip"
                ></button>
              </div>
            </div>

            <div class="person-info-centered">
              <div class="person-name">
                {{
                  previewResult.name ||
                    ('SEARCH_GLOBAL_MODAL.UNNAMED_PERSON' | translate)
                }}
              </div>
              <div class="person-recognitions">
                {{
                  previewResult.appearance && previewResult.appearance > 0
                    ? previewResult.appearance +
                      ' ' +
                      (previewResult.appearance === 1
                        ? ('SEARCH_GLOBAL_MODAL.RECOGNITION' | translate)
                        : ('SEARCH_GLOBAL_MODAL.RECOGNITIONS' | translate))
                    : ('SEARCH_GLOBAL_MODAL.NO_RECOGNITIONS' | translate)
                }}
              </div>
            </div>

            <div
              class="person-details-info"
              *ngIf="previewResult.raw?.cameras?.[0] || previewResult.timestamp"
            >
              <div class="person-info-row">
                <div
                  class="person-info-item"
                  *ngIf="previewResult.raw?.cameras?.[0]"
                >
                  <span class="ui-icon-camera ui-icon-20"></span>
                  <div class="person-info-text">
                    <span class="info-text">{{
                      previewResult.raw.cameras[0].cameraName ||
                        ('SEARCH_GLOBAL_MODAL.UNKNOWN_CAMERA' | translate)
                    }}</span>
                    <span class="info-text-secondary">{{
                      previewResult.raw.cameras[0].locationName ||
                        ('SEARCH_GLOBAL_MODAL.UNKNOWN_LOCATION' | translate)
                    }}</span>
                  </div>
                </div>
                <div
                  class="divider-vertical"
                  *ngIf="
                    previewResult.raw?.cameras?.[0] && previewResult.timestamp
                  "
                ></div>
                <div class="person-info-item" *ngIf="previewResult.timestamp">
                  <mat-icon svgIcon="eye"></mat-icon>
                  <div class="person-info-text">
                    <span class="info-text">{{
                      previewResult.timestamp | date: 'MMMM d,'
                    }}</span>
                    <span class="info-text-secondary">{{
                      previewResult.timestamp | date: 'h:mm a y'
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Stats -->
          <div
            class="location-summary"
            *ngIf="
              previewResult.raw?.edges?.length > 0 ||
              previewResult.raw?.cameras?.length > 0 ||
              previewResult.raw?.locations?.length > 0
            "
          >
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.edges?.length > 0"
              [class.active]="activePeopleView === 'edges'"
              (click)="setActivePeopleView('edges'); $event.stopPropagation()"
            >
              <span class="count">{{ previewResult.raw.edges.length }}</span>
              <span class="label">{{
                previewResult.raw.edges.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.EDGE' | translate)
                  : ('SEARCH_GLOBAL_MODAL.EDGES' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.cameras?.length > 0"
              [class.active]="activePeopleView === 'cameras'"
              (click)="setActivePeopleView('cameras'); $event.stopPropagation()"
            >
              <span class="count">{{ previewResult.raw.cameras.length }}</span>
              <span class="label">{{
                previewResult.raw.cameras.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                  : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.locations?.length > 0"
              [class.active]="activePeopleView === 'locations'"
              (click)="
                setActivePeopleView('locations'); $event.stopPropagation()
              "
            >
              <span class="count">{{
                previewResult.raw.locations.length
              }}</span>
              <span class="label">{{
                previewResult.raw.locations.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.LOCATION' | translate)
                  : ('SEARCH_GLOBAL_MODAL.LOCATIONS' | translate)
              }}</span>
            </div>
          </div>

          <!-- Active List Display -->
          <div
            class="location-content"
            *ngIf="
              previewResult.raw?.edges?.length > 0 ||
              previewResult.raw?.cameras?.length > 0 ||
              previewResult.raw?.locations?.length > 0
            "
          >
            <!-- Edge List -->
            <div *ngIf="activePeopleView === 'edges'">
              <ng-container
                *ngTemplateOutlet="
                  edgeList;
                  context: {
                    edges: previewResult.raw?.edges,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Camera List -->
            <div *ngIf="activePeopleView === 'cameras'">
              <ng-container
                *ngTemplateOutlet="
                  cameraList;
                  context: {
                    cameras: previewResult.raw?.cameras,
                    showEdge: false,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Location List -->
            <div *ngIf="activePeopleView === 'locations'">
              <ng-container
                *ngTemplateOutlet="
                  locationList;
                  context: {
                    locations: previewResult.raw?.locations,
                    showHeader: false,
                    useSearchNavigation: true,
                  }
                "
              ></ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- Building Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'building'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.BUILDING_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">{{ previewResult.name }}</div>
            <div class="preview-actions">
              <!--                          NEEDS TO IMPLEMENTED URL EDIT IN UI-->
              <!--                            <button class="ui-icon-edit ui-icon-button no-border"-->
              <!--                                    disableButtonPermissionGuard-->
              <!--                                    [permission]="permissions.BuildingEdit"-->
              <!--                                    [tooltip]="'SEARCH_GLOBAL_MODAL.EDIT_BUILDING' | translate"-->
              <!--                                    [disabledTooltip]="'SEARCH_GLOBAL_MODAL.NO_EDIT_PERMISSIONS' | translate"-->
              <!--                                    matTooltip matTooltipClass="sidebar-tooltip"-->
              <!--                                    (click)="editBuilding(previewResult)"-->
              <!--                            >-->
              <!--                            </button>-->
            </div>
          </div>
          <div class="preview-description" *ngIf="previewResult.subInfo">
            {{ previewResult.subInfo }}
          </div>

          <!-- Summary Stats -->
          <div
            class="location-summary"
            *ngIf="
              previewResult.raw?.floors?.length > 0 ||
              previewResult.raw?.edges?.length > 0 ||
              previewResult.raw?.cameras?.length > 0 ||
              previewResult.raw?.locations?.length > 0
            "
          >
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.floors?.length > 0"
              [class.active]="activeBuildingView === 'floors'"
              (click)="
                setActiveBuildingView('floors'); $event.stopPropagation()
              "
            >
              <span class="count">{{ previewResult.raw.floors.length }}</span>
              <span class="label">{{
                previewResult.raw.floors.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.FLOOR' | translate)
                  : ('SEARCH_GLOBAL_MODAL.FLOORS' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.edges?.length > 0"
              [class.active]="activeBuildingView === 'edges'"
              (click)="setActiveBuildingView('edges'); $event.stopPropagation()"
            >
              <span class="count">{{ previewResult.raw.edges.length }}</span>
              <span class="label">{{
                previewResult.raw.edges.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.EDGE' | translate)
                  : ('SEARCH_GLOBAL_MODAL.EDGES' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.cameras?.length > 0"
              [class.active]="activeBuildingView === 'cameras'"
              (click)="
                setActiveBuildingView('cameras'); $event.stopPropagation()
              "
            >
              <span class="count">{{ previewResult.raw.cameras.length }}</span>
              <span class="label">{{
                previewResult.raw.cameras.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                  : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.locations?.length > 0"
              [class.active]="activeBuildingView === 'locations'"
              (click)="
                setActiveBuildingView('locations'); $event.stopPropagation()
              "
            >
              <span class="count">{{
                previewResult.raw.locations.length
              }}</span>
              <span class="label">{{
                previewResult.raw.locations.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.LOCATION' | translate)
                  : ('SEARCH_GLOBAL_MODAL.LOCATIONS' | translate)
              }}</span>
            </div>
          </div>

          <!-- Active List Display -->
          <div
            class="location-content"
            *ngIf="
              previewResult.raw?.floors?.length > 0 ||
              previewResult.raw?.edges?.length > 0 ||
              previewResult.raw?.cameras?.length > 0 ||
              previewResult.raw?.locations?.length > 0
            "
          >
            <!-- Floor List -->
            <div *ngIf="activeBuildingView === 'floors'">
              <ng-container
                *ngTemplateOutlet="
                  floorList;
                  context: {
                    floors: previewResult.raw?.floors,
                    showHeader: false,
                    buildingId: previewResult.buildingId || previewResult._id,
                  }
                "
              ></ng-container>
            </div>

            <!-- Edge List -->
            <div *ngIf="activeBuildingView === 'edges'">
              <ng-container
                *ngTemplateOutlet="
                  edgeList;
                  context: {
                    edges: previewResult.raw?.edges,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Camera List -->
            <div *ngIf="activeBuildingView === 'cameras'">
              <ng-container
                *ngTemplateOutlet="
                  cameraList;
                  context: {
                    cameras: previewResult.raw?.cameras,
                    showEdge: false,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Location List -->
            <div *ngIf="activeBuildingView === 'locations'">
              <ng-container
                *ngTemplateOutlet="
                  locationList;
                  context: {
                    locations: previewResult.raw?.locations,
                    showHeader: false,
                    useSearchNavigation: true,
                  }
                "
              ></ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- Floor Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'floor'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.FLOOR_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">{{ previewResult.name }}</div>
            <div class="preview-actions">
              <!--              NEEDS TO IMPLEMENT URL MODAL IN UI-->
              <!--              <button class="ui-icon-edit ui-icon-button no-border"-->
              <!--                      disableButtonPermissionGuard-->
              <!--                      [permission]="permissions.FloorEdit"-->
              <!--                      [tooltip]="'SEARCH_GLOBAL_MODAL.EDIT_FLOOR' | translate"-->
              <!--                      [disabledTooltip]="'SEARCH_GLOBAL_MODAL.NO_EDIT_PERMISSIONS' | translate"-->
              <!--                      matTooltip matTooltipClass="sidebar-tooltip"-->
              <!--                      (click)="editFloor(previewResult)"-->
              <!--              >-->
              <!--              </button>-->
            </div>
          </div>
          <div class="preview-description" *ngIf="previewResult.subInfo">
            {{ previewResult.subInfo }}
          </div>

          <!-- Interactive Floor Preview with Camera Positioning -->
          <div
            class="floor-interactive-preview"
            *ngIf="previewResult.floorImage"
            (mouseenter)="onFloorPreviewMouseEnter()"
            (mouseleave)="onFloorPreviewMouseLeave()"
          >
            <div
              class="floor-preview-wrapper"
              [appAuthBg]="resolveFloorImageUrl(previewResult.floorImage)"
              [appAuthBgFallback]="
                resolveFloorImageFallback(previewResult.floorImage)
              "
            >
              <!-- Camera Preview Components -->
              <div
                *ngFor="let camera of previewResult.raw?.cameras"
                class="camera-preview-square"
                [style.left]="camera.position.x * 100 + '%'"
                [style.top]="camera.position.y * 100 + '%'"
                [matTooltip]="
                  camera.cameraName ||
                  ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                "
                matTooltipClass="sidebar-tooltip"
                (mouseenter)="onCameraConeMouseEnter()"
                (mouseleave)="onCameraConeMouseLeave()"
              >
                <div
                  class="camera-preview-center"
                  [style.transform]="
                    'rotate(' + (camera.rotation || 0) + 'deg)'
                  "
                >
                  <div
                    class="camera-preview-visual"
                    [style.width]="getCameraSize(camera.radius) + 'px'"
                    [style.height]="getCameraSize(camera.radius) + 'px'"
                  >
                    <div class="camera-preview-dot"></div>
                    <div class="camera-preview-radius"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Stats -->
          <div
            class="location-summary"
            *ngIf="
              previewResult.raw?.edges?.length > 0 ||
              previewResult.raw?.cameras?.length > 0 ||
              previewResult.raw?.locations?.length > 0
            "
          >
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.edges?.length > 0"
              [class.active]="activeFloorView === 'edges'"
              (click)="setActiveFloorView('edges'); $event.stopPropagation()"
            >
              <span class="count">{{ previewResult.raw.edges.length }}</span>
              <span class="label">{{
                previewResult.raw.edges.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.EDGE' | translate)
                  : ('SEARCH_GLOBAL_MODAL.EDGES' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.cameras?.length > 0"
              [class.active]="activeFloorView === 'cameras'"
              (click)="setActiveFloorView('cameras'); $event.stopPropagation()"
            >
              <span class="count">{{ previewResult.raw.cameras.length }}</span>
              <span class="label">{{
                previewResult.raw.cameras.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                  : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.locations?.length > 0"
              [class.active]="activeFloorView === 'locations'"
              (click)="
                setActiveFloorView('locations'); $event.stopPropagation()
              "
            >
              <span class="count">{{
                previewResult.raw.locations.length
              }}</span>
              <span class="label">{{
                previewResult.raw.locations.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.LOCATION' | translate)
                  : ('SEARCH_GLOBAL_MODAL.LOCATIONS' | translate)
              }}</span>
            </div>
          </div>

          <!-- Active List Display -->
          <div
            class="location-content"
            *ngIf="
              previewResult.raw?.edges?.length > 0 ||
              previewResult.raw?.cameras?.length > 0 ||
              previewResult.raw?.locations?.length > 0
            "
          >
            <!-- Edge List -->
            <div *ngIf="activeFloorView === 'edges'">
              <ng-container
                *ngTemplateOutlet="
                  edgeList;
                  context: {
                    edges: previewResult.raw?.edges,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Camera List -->
            <div *ngIf="activeFloorView === 'cameras'">
              <ng-container
                *ngTemplateOutlet="
                  cameraList;
                  context: {
                    cameras: previewResult.raw?.cameras,
                    showEdge: false,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Location List -->
            <div *ngIf="activeFloorView === 'locations'">
              <ng-container
                *ngTemplateOutlet="
                  locationList;
                  context: {
                    locations: previewResult.raw?.locations,
                    showHeader: false,
                    useSearchNavigation: true,
                  }
                "
              ></ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- Wall Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'wall'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.WALL_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">{{ previewResult.name }}</div>
            <div class="preview-actions">
              <div class="preview-private" *ngIf="previewResult.raw?.isPrivate">
                <i
                  class="icon ui-icon-lock"
                  [matTooltip]="'SEARCH_GLOBAL_MODAL.PRIVATE_WALL' | translate"
                  matTooltipClass="sidebar-tooltip"
                ></i>
              </div>
              <button
                class="ui-icon-edit ui-icon-button no-border"
                disableButtonPermissionGuard
                [permission]="permissions.WallEdit"
                [tooltip]="'SEARCH_GLOBAL_MODAL.EDIT_WALL' | translate"
                [disabledTooltip]="
                  'SEARCH_GLOBAL_MODAL.NO_EDIT_PERMISSIONS' | translate
                "
                matTooltip
                matTooltipClass="sidebar-tooltip"
                (click)="editWall(previewResult)"
              ></button>
            </div>
          </div>
          <div class="preview-description" *ngIf="previewResult.subInfo">
            {{ previewResult.subInfo }}
          </div>

          <!-- Wall Stats -->
          <div class="preview-stats">
            <div class="preview-stat">
              <i class="icon ui-icon-wall"></i>
              <span
                >{{ previewResult.raw?.setCount || 0 }}
                {{
                  (previewResult.raw?.setCount || 0) === 1
                    ? ('SEARCH_GLOBAL_MODAL.SET' | translate)
                    : ('SEARCH_GLOBAL_MODAL.SETS' | translate)
                }}</span
              >
            </div>
            <div
              class="preview-stat"
              *ngIf="!previewResult.raw?.cameras?.length"
            >
              <i class="icon ui-icon-camera"></i>
              <span
                >{{ previewResult.raw?.cameraCount || 0 }}
                {{
                  (previewResult.raw?.cameraCount || 0) === 1
                    ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                    : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
                }}</span
              >
            </div>
            <div class="preview-stat">
              <i class="ui-icon-device menu-icon"></i>
              <span
                >{{ 'SEARCH_GLOBAL_MODAL.MOBILE' | translate }}
                {{
                  previewResult.raw?.isMobileSupported
                    ? ('SEARCH_GLOBAL_MODAL.MOBILE_SUPPORTED' | translate)
                    : ('SEARCH_GLOBAL_MODAL.MOBILE_NOT_SUPPORTED' | translate)
                }}</span
              >
            </div>
            <div class="preview-stat" *ngIf="previewResult.raw?.isAlertWall">
              <i class="icon ui-icon-notifications"></i>
              <span *ngIf="previewResult.raw?.hasAllEvents">{{
                'SEARCH_GLOBAL_MODAL.ALL_ORGANIZATION_ALERTS' | translate
              }}</span>
              <span
                *ngIf="
                  !previewResult.raw?.hasAllEvents &&
                  previewResult.raw?.events?.length
                "
              >
                {{ previewResult.raw.events.length }}
                {{
                  previewResult.raw.events.length === 1
                    ? ('SEARCH_GLOBAL_MODAL.ALERT' | translate)
                    : ('SEARCH_GLOBAL_MODAL.ALERTS' | translate)
                }}
              </span>
              <span
                *ngIf="
                  !previewResult.raw?.hasAllEvents &&
                  !previewResult.raw?.events?.length
                "
                >{{ 'SEARCH_GLOBAL_MODAL.ALERT_WALL' | translate }}</span
              >
            </div>
            <div class="preview-stat" *ngIf="previewResult.raw?.hasYoutube">
              <i class="icon ui-icon-youtube"></i>
              <span>{{
                'SEARCH_GLOBAL_MODAL.YOUTUBE_CONTENT' | translate
              }}</span>
            </div>
          </div>

          <!-- Interactive Navigation -->
          <div class="location-summary">
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.edges?.length > 0"
              [class.active]="activeWallView === 'edges'"
              (click)="setActiveWallView('edges'); $event.stopPropagation()"
            >
              <span class="count">{{ previewResult.raw.edges.length }}</span>
              <span class="label">{{
                previewResult.raw.edges.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.EDGE' | translate)
                  : ('SEARCH_GLOBAL_MODAL.EDGES' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.cameras?.length > 0"
              [class.active]="activeWallView === 'cameras'"
              (click)="setActiveWallView('cameras'); $event.stopPropagation()"
            >
              <span class="count">{{ previewResult.raw.cameras.length }}</span>
              <span class="label">{{
                previewResult.raw.cameras.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                  : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
              }}</span>
            </div>
            <div
              class="summary-item"
              *ngIf="previewResult.raw?.events?.length > 0"
              [class.active]="activeWallView === 'events'"
              (click)="setActiveWallView('events'); $event.stopPropagation()"
            >
              <span class="count">{{ previewResult.raw.events.length }}</span>
              <span class="label">{{
                previewResult.raw.events.length === 1
                  ? ('SEARCH_GLOBAL_MODAL.EVENT' | translate)
                  : ('SEARCH_GLOBAL_MODAL.EVENTS' | translate)
              }}</span>
            </div>
          </div>

          <!-- Active List Display -->
          <div class="location-content">
            <!-- Edge List -->
            <div *ngIf="activeWallView === 'edges'">
              <ng-container
                *ngTemplateOutlet="
                  edgeList;
                  context: {
                    edges: previewResult.raw?.edges,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Camera List -->
            <div *ngIf="activeWallView === 'cameras'">
              <ng-container
                *ngTemplateOutlet="
                  cameraList;
                  context: {
                    cameras: previewResult.raw?.cameras,
                    showEdge: false,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>

            <!-- Events List -->
            <div *ngIf="activeWallView === 'events'">
              <ng-container
                *ngTemplateOutlet="
                  eventList;
                  context: {
                    events: previewResult.raw?.events,
                    showHeader: false,
                  }
                "
              ></ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- Alarm Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'alarm'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.ALARM_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">{{ previewResult.name }}</div>
            <div class="preview-actions">
              <button
                class="ui-icon-edit ui-icon-button no-border"
                disableButtonPermissionGuard
                [permission]="permissions.CallCenter"
                [tooltip]="'SEARCH_GLOBAL_MODAL.EDIT_ALARM' | translate"
                [disabledTooltip]="
                  'SEARCH_GLOBAL_MODAL.NO_EDIT_PERMISSIONS' | translate
                "
                matTooltip
                matTooltipClass="sidebar-tooltip"
                (click)="editAlarm(previewResult)"
              ></button>
            </div>
          </div>
          <div class="preview-description" *ngIf="previewResult.subInfo">
            {{ previewResult.subInfo }}
          </div>

          <!-- Quick Actions Section -->
          <div class="alarm-quick-actions">
            <div class="section-header">
              <h5>{{ 'SEARCH_GLOBAL_MODAL.QUICK_ACTIONS' | translate }}</h5>
            </div>
            <div class="quick-action-card">
              <div class="quick-action-info">
                <div class="quick-action-label">
                  {{ 'SEARCH_GLOBAL_MODAL.ALARM_STATE' | translate }}
                </div>
              </div>
              <div class="quick-action-control">
                <alarm-schedule-controls
                  [alarmId]="previewResult.alarmId"
                  [alarmState]="previewResult.state"
                >
                </alarm-schedule-controls>
              </div>
            </div>
          </div>

          <div class="alarm-separator"></div>

          <!-- State Section -->
          <div class="alarm-state-section">
            <div class="section-header">
              <h5>{{ 'SEARCH_GLOBAL_MODAL.STATE' | translate }}</h5>
            </div>
            <div class="alarm-status">
              <div class="alarm-state-display">
                <span
                  class="state-text"
                  [class.armed]="previewResult.calculatedState === 0"
                  [class.disarmed]="previewResult.calculatedState === 1"
                  [class.scheduled]="previewResult.calculatedState === 2"
                >
                  {{
                    alarmStatusChangeString[previewResult.calculatedState]
                      | translate
                  }}
                </span>
              </div>
              <div class="since-info">
                <span>{{ 'SEARCH_GLOBAL_MODAL.SINCE' | translate }}</span>
                <ui-user-settings-date
                  [date]="previewResult.stateUpdatedAt | unixTs"
                ></ui-user-settings-date>
              </div>
            </div>
          </div>

          <div class="alarm-separator"></div>

          <!-- Overview Section -->
          <div class="alarm-overview">
            <div class="section-header">
              <h5>{{ 'SEARCH_GLOBAL_MODAL.OVERVIEW' | translate }}</h5>
            </div>
            <div class="preview-stats">
              <div class="preview-stat">
                <i class="icon ui-icon-notifications"></i>
                <span
                  >{{ previewResult.raw?.events?.length || 0 }}
                  {{
                    (previewResult.raw?.events?.length || 0) === 1
                      ? ('SEARCH_GLOBAL_MODAL.EVENT' | translate)
                      : ('SEARCH_GLOBAL_MODAL.EVENTS' | translate)
                  }}</span
                >
              </div>
              <div class="preview-stat">
                <i class="icon ui-icon-camera"></i>
                <span
                  >{{ previewResult.raw?.cameras?.length || 0 }}
                  {{
                    (previewResult.raw?.cameras?.length || 0) === 1
                      ? ('SEARCH_GLOBAL_MODAL.CAMERA' | translate)
                      : ('SEARCH_GLOBAL_MODAL.CAMERAS' | translate)
                  }}</span
                >
              </div>
            </div>
          </div>

          <!-- Configuration Section -->
          <div
            class="alarm-configuration"
            *ngIf="
              previewResult.type === 'alarm' &&
              (previewResult.scheduleId !== undefined ||
                previewResult.reArmMin !== undefined ||
                previewResult.disArmMin !== undefined ||
                previewResult.notificationScheduleId !== undefined)
            "
          >
            <div class="section-header">
              <h5>{{ 'SEARCH_GLOBAL_MODAL.CONFIGURATION' | translate }}</h5>
            </div>

            <!-- Arm Schedule -->
            <div class="config-item">
              <div class="config-label">
                <i class="icon ui-icon-calendar ui-icon"></i>
                <span>{{
                  'SEARCH_GLOBAL_MODAL.ARM_SCHEDULE' | translate
                }}</span>
              </div>
              <div
                class="config-value clickable"
                (click)="navigateToSchedule(previewResult.scheduleId)"
                *ngIf="
                  previewResult.scheduleId !== undefined &&
                  previewResult.scheduleId !== ''
                "
              >
                <span>{{ 'SEARCH_GLOBAL_MODAL.CONFIGURED' | translate }}</span>
              </div>
              <div
                class="config-value"
                *ngIf="
                  !previewResult.scheduleId || previewResult.scheduleId === ''
                "
              >
                <span>{{ 'SEARCH_GLOBAL_MODAL.ALL_TIMES' | translate }}</span>
              </div>
            </div>

            <!-- Auto Rearm -->
            <div
              class="config-item"
              *ngIf="previewResult.reArmMin !== undefined"
            >
              <div class="config-label">
                <i class="icon ui-icon-notifications"></i>
                <span>{{ 'SEARCH_GLOBAL_MODAL.AUTO_REARM' | translate }}</span>
              </div>
              <div class="config-value" *ngIf="previewResult.reArmMin === 0">
                <span>{{ 'SEARCH_GLOBAL_MODAL.DISABLED' | translate }}</span>
              </div>
              <div class="config-value" *ngIf="previewResult.reArmMin > 0">
                <span
                  >{{ previewResult.reArmMin }}
                  {{ 'CALENDAR.MINUTES' | translate }}</span
                >
              </div>
            </div>

            <!-- Auto Disarm -->
            <div
              class="config-item"
              *ngIf="previewResult.disArmMin !== undefined"
            >
              <div class="config-label">
                <i class="icon ui-icon-notifications"></i>
                <span>{{ 'SEARCH_GLOBAL_MODAL.AUTO_DISARM' | translate }}</span>
              </div>
              <div class="config-value" *ngIf="previewResult.disArmMin === 0">
                <span>{{ 'SEARCH_GLOBAL_MODAL.DISABLED' | translate }}</span>
              </div>
              <div class="config-value" *ngIf="previewResult.disArmMin > 0">
                <span
                  >{{ previewResult.disArmMin }}
                  {{ 'CALENDAR.MINUTES' | translate }}</span
                >
              </div>
            </div>

            <!-- Notification Window -->
            <div
              class="config-item"
              *ngIf="previewResult.notificationScheduleId !== undefined"
            >
              <div class="config-label">
                <i class="icon ui-icon-calendar ui-icon"></i>
                <span>{{
                  'SEARCH_GLOBAL_MODAL.NOTIFICATION_WINDOW' | translate
                }}</span>
              </div>
              <div
                class="config-value clickable"
                (click)="
                  navigateToSchedule(previewResult.notificationScheduleId)
                "
                *ngIf="previewResult.notificationScheduleId !== ''"
              >
                <span>{{ 'SEARCH_GLOBAL_MODAL.CONFIGURED' | translate }}</span>
              </div>
              <div
                class="config-value"
                *ngIf="previewResult.notificationScheduleId === ''"
              >
                <span>{{ 'SEARCH_GLOBAL_MODAL.ALL_TIMES' | translate }}</span>
              </div>
            </div>
          </div>

          <!-- Events associated with this alarm  -->
          <ng-container
            *ngTemplateOutlet="
              eventList;
              context: { events: previewResult.raw?.events }
            "
          ></ng-container>
        </div>
      </div>

      <!-- Page Preview -->
      <div class="preview-content" *ngIf="previewResult.type === 'page'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.PAGE_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">{{ previewResult.name }}</div>
            <div class="preview-actions">
              <button
                class="ui-icon-navigate ui-icon-button no-border"
                (click)="navigateToPage(previewResult)"
                [matTooltip]="
                  'SEARCH_GLOBAL_MODAL.NAVIGATE_TO_PAGE' | translate
                "
                matTooltipClass="sidebar-tooltip"
              ></button>
            </div>
          </div>
          <div class="preview-description" *ngIf="previewResult.description">
            {{ previewResult.description }}
          </div>
        </div>
      </div>

      <!-- Docs (Articles) Preview disabled for now
      <div class="preview-content" *ngIf="previewResult.type === 'article'">
        <h4>{{ 'SEARCH_GLOBAL_MODAL.DOCS_PANEL' | translate }}</h4>
        <div class="preview-info">
          <div class="preview-header">
            <div class="preview-name">{{ previewResult.displayName }}</div>
            <div class="preview-actions">
              <button class="ui-icon-fullscreen ui-icon-button no-border icon-hover"
                      [matTooltip]="'SEARCH_GLOBAL_MODAL.FULLSCREEN' | translate"
                      matTooltipClass="sidebar-tooltip"
                      (click)="openDocsFullscreen(previewResult)"></button>

              <button class="ui-icon-new-tab ui-icon-button no-border icon-hover"
                      [matTooltip]="'SEARCH_GLOBAL_MODAL.VIEW_ORIGINAL' | translate"
                      matTooltipClass="sidebar-tooltip"
                      (click)="viewDocsOriginal(previewResult)"></button>
            </div>
          </div>
          @if (previewResult.raw?.youtubeEmbedUrls?.length) {
            <div class="preview-youtube-players">
              @for (embedUrl of previewResult.raw.youtubeEmbedUrls; track embedUrl) {
                <div class="preview-youtube-player">
                  <iframe
                    [src]="embedUrl | youtubeEmbedUrl"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowfullscreen>
                  </iframe>
                </div>
              }
            </div>
          }
          <div class="preview-description" #docsPreview [innerHTML]="getArticlePreviewHtml(previewResult)"></div>
        </div>
      </div>
      -->
    </div>
  </div>

  <div
    *ngIf="
      flatResults.length === 0 &&
      hasActiveSearchTerm() &&
      !isCommandMode &&
      !isAiMode &&
      !isAiPromptDraft()
    "
    class="no-results"
  >
    <span *ngIf="!isCategoryFilterMode"
      >{{ 'SEARCH_GLOBAL_MODAL.NO_RESULTS_FOUND' | translate }} "{{
        searchTerm.trim()
      }}".</span
    >
    <span *ngIf="isCategoryFilterMode"
      >{{
        'SEARCH_GLOBAL_MODAL.NO_RESULTS_FOUND_IN_CATEGORIES' | translate
      }}
      "{{ getCategorySearchTerm() }}" {{ 'SEARCH_GLOBAL_MODAL.IN' | translate }}
      {{ selectedCategories.join(', ') }}.</span
    >
  </div>

  <div *ngIf="searchTerm && !isAiPromptDraft()" class="search-footer">
    <ng-container *ngIf="selectShowProgressing$ | async; else syncedState">
      <div class="search-footer-progress">
        <mat-progress-bar
          class="search-footer-progress-bar"
          mode="determinate"
          [value]="(selectSyncProgress$ | async) ?? 0"
        ></mat-progress-bar>
      </div>
    </ng-container>

    <ng-template #syncedState>
      <div class="search-footer-meta">
        <span>{{
          'ACCOUNT.GLOBAL_SEARCH_SETTINGS.LAST_SYNCED_AT' | translate
        }}</span>
        <span class="search-footer-date">
          {{
            (selectLastSyncedAt$ | async)
              ? (selectLastSyncedAt$ | async | date: 'dd-MM-YY HH:mm:ss')
              : ' - '
          }}
        </span>
      </div>
    </ng-template>

    <button
      *ngIf="!(selectShowProgressing$ | async)"
      type="button"
      class="search-footer-sync-button"
      (click)="sync()"
    >
      {{ 'ACCOUNT.GLOBAL_SEARCH_SETTINGS.TOOLTIPS.SYNC' | translate }}
    </button>
  </div>

  <!-- Custom Match Reasons Tooltip  -->
  <div
    class="match-reasons-tooltip"
    *ngIf="isMatchReasonsTooltipVisible"
    [style.left.px]="tooltipPosition.x"
    [style.top.px]="tooltipPosition.y"
    (mouseenter)="onTooltipMouseEnter()"
    (mouseleave)="onTooltipMouseLeave()"
  >
    <div class="tooltip-header">
      <i class="icon ui-icon-search"></i>
      <span>{{ 'SEARCH_GLOBAL_MODAL.ALL_MATCH_REASONS' | translate }}</span>
    </div>
    <div class="tooltip-content" #tooltipContent>
      <div
        class="match-group"
        *ngFor="let group of groupedMatchReasonsWithHighlighting | keyvalue"
      >
        <div class="group-header">{{ group.key }}:</div>
        <div class="group-items">
          <div
            class="group-item"
            *ngFor="let item of group.value; let i = index"
          >
            {{ i + 1 }}. <span [innerHTML]="item"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
